import React, { ChangeEvent, FC, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, Controller, useWatch, FieldValues } from "react-hook-form";
import { debounce, isEmpty } from "lodash";
import {
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import {
  DraftNoteDto,
  EntityType,
  FileMetaDataDto,
  FormQuestionDto,
  UploadedFileFeature,
} from "@green0meter/api-client";

import { FileListItem, ConfirmationModal } from "@components/common";
import { TextField } from "@components/base";
import {
  UploadedFileInfoModel,
  useCreateUpdateDraftNote,
  useGetFormDraftNote,
  useUploadFile,
} from "@api";
import { useMultistepFormStore } from "@components/common/MultistepForm/MultistepForm.store";
import { useUserStore } from "@store/user";
import { AIImportDialog } from "@components/common/MultistepForm/components/TopNav/components/ActionsDropdown/components/AIImport/AIImportDialog";

export interface FileItem {
  name: string;
  url: string;
  uploadedBy: string;
  uploadedAt: string;
}

interface Note {
  question: FormQuestionDto;
  onSaved?: () => void;
}

const toFileId = (
  attachment: FileMetaDataDto | string | null | undefined,
): string | null => {
  if (!attachment) return null;
  if (typeof attachment === "string") {
    const trimmed = attachment.trim();

    return trimmed ? trimmed : null;
  }

  return attachment.fileId ?? null;
};

const Note: FC<Note> = ({ question, onSaved }) => {
  const { t } = useTranslation();
  const { control, setValue, getValues, resetField } = useFormContext();
  const [note, setNote] = useState<DraftNoteDto>();
  const [showSaved, setShowSaved] = useState(false);
  const [localValue, setLocalValue] = useState<string>("");
  const [showAIExtractModal, setShowAIExtractModal] = useState(false);
  const [uploadedFilesForAI, setUploadedFilesForAI] = useState<File[]>([]);
  const [aiImportDialogOpen, setAIImportDialogOpen] = useState(false);

  const currentCommentValue = useWatch<FieldValues, string>({
    control,
    name: `${question.code}-comment`,
    defaultValue: "",
  });

  useEffect(() => {
    setLocalValue(currentCommentValue ?? "");
  }, [currentCommentValue]);

  const {
    formSettings: { formBasics },
  } = useMultistepFormStore();
  const { user } = useUserStore();
  const { formDraftNote, isLoadingFormDraftNote } = useGetFormDraftNote(
    user,
    formBasics.id,
  );
  const { createUpdateDraftNoteAsync } = useCreateUpdateDraftNote(
    user,
    formBasics.id,
  );
  const { uploadFileAsync, uploadFileEnabled, isPendingUploadFile } =
    useUploadFile(user);

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (!user || !event.target.files) return;

    try {
      const newFiles = Array.from(event.target.files);
      const uploadedFiles: UploadedFileInfoModel[] = await Promise.all(
        newFiles.map((file) =>
          uploadFileAsync({
            file,
            entityId: formBasics.id || "",
            databaseEntityType: EntityType.Form,
            uploadedFileFeature: UploadedFileFeature.FormData,
          }),
        ),
      );

      const existingAttachments =
        getValues(`${question.code}-attachments`) || note?.attachments || [];
      const existingFileIds = existingAttachments
        .map((attachment: FileMetaDataDto | string) => toFileId(attachment))
        .filter((id: string | null): id is string => !!id);

      const newFileIds = uploadedFiles.map((file) => file.fileId);
      const allFileIds = [...existingFileIds, ...newFileIds];

      const updated = await createUpdateDraftNoteAsync({
        ...(note ?? {}),
        formId: formBasics.id,
        questionCode: question.code,
        attachments: allFileIds,
        value: getValues(`${question.code}-comment`) ?? "",
        valueUpdatedDateTime: note?.valueUpdatedDateTime,
      });

      setNote(updated);
      onSaved?.();

      const aiExtractableExtensions = [
        ".pdf",
        ".doc",
        ".docx",
        ".txt",
        ".xls",
        ".xlsx",
        ".rtf",
        ".odt",
      ];

      const extractableFiles = newFiles.filter((file) =>
        aiExtractableExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)),
      );

      if (!isEmpty(extractableFiles)) {
        setUploadedFilesForAI(extractableFiles);
        setShowAIExtractModal(true);
      }
    } catch (error) {
      console.error("Save draft note failed", error);
    }
  };

  useEffect(() => {
    if (formDraftNote && formDraftNote.length > 0) {
      const currentNote = formDraftNote.find(
        (note) => note.questionCode === question.code,
      );

      setNote(currentNote);

      setValue(`${question.code}-comment`, currentNote?.value ?? "", {
        shouldDirty: false,
      });

      setValue(`${question.code}-attachments`, currentNote?.attachments || [], {
        shouldDirty: false,
      });
    } else {
      setNote(undefined);

      setValue(`${question.code}-comment`, "", { shouldDirty: false });
      setValue(`${question.code}-attachments`, [], { shouldDirty: false });
    }
  }, [formDraftNote]);

  const saveDraftNote = async (value: string): Promise<void> => {
    const attachments =
      (getValues(`${question.code}-attachments`) as Array<
        FileMetaDataDto | string
      >) || [];

    await createUpdateDraftNoteAsync({
      formId: formBasics.id,
      questionCode: question.code,
      value: value || "",
      attachments: attachments
        .map((attachment) => toFileId(attachment))
        .filter((id: string | null): id is string => !!id),
      valueUpdatedDateTime: note?.valueUpdatedDateTime,
    });

    resetField(`${question.code}-comment`, {
      defaultValue: value || "",
    });
    resetField(`${question.code}-attachments`, {
      defaultValue: attachments,
    });

    setShowSaved(true);

    setTimeout(() => setShowSaved(false), 1000);

    onSaved?.();
  };

  // Create a debounced version of saveDraftNote for auto-save
  const debouncedSaveDraftNote = useCallback(debounce(saveDraftNote, 1000), [
    formBasics.id,
    question.code,
    note?.valueUpdatedDateTime,
  ]);

  // Auto-save when comment value changes
  useEffect(() => {
    const fieldState = control.getFieldState(`${question.code}-comment`);

    if (
      fieldState.isDirty &&
      !isLoadingFormDraftNote &&
      currentCommentValue !== (note?.value ?? "")
    ) {
      debouncedSaveDraftNote(currentCommentValue);
    }
  }, [
    currentCommentValue,
    note?.value,
    isLoadingFormDraftNote,
    debouncedSaveDraftNote,
    control,
    question.code,
  ]);

  const handleDeleteFile = async (
    note: DraftNoteDto,
    file: FileMetaDataDto,
  ): Promise<void> => {
    const remainingFileIds = note.attachments
      .filter((f) => f.fileId !== file.fileId)
      .map((f) => f.fileId)
      .filter((id: string | null | undefined): id is string => !!id);

    const updated = await createUpdateDraftNoteAsync({
      formId: formBasics.id,
      questionCode: question.code,
      value: note.value ?? "",
      attachments: remainingFileIds,
      valueUpdatedDateTime: note.valueUpdatedDateTime || new Date().toISOString(),
    });

    setNote(updated);

    onSaved?.();
  };

  const handleAIExtractConfirm = (): void => {
    setShowAIExtractModal(false);
    setAIImportDialogOpen(true);
  };

  const handleAIExtractCancel = (): void => {
    setShowAIExtractModal(false);
    setUploadedFilesForAI([]);
  };

  const handleAIImportDialogClose = (): void => {
    setAIImportDialogOpen(false);
    setUploadedFilesForAI([]);
  };

  return (
    <AccordionDetails
      sx={{
        display: "flex",
        justifyContent: "space-between",
        margin: "24px 0",
        padding: "0",
      }}
    >
      <Box
        sx={(theme) => ({
          border: "1px solid #ccc",
          borderRadius: "12px",
          width: "100%",
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
        })}
      >
        <Box
          sx={({ shadows }) => ({
            backgroundColor: "white",
            paddingX: 2,
            paddingY: 1.5,
            height: 40,
            boxShadow: shadows[6],
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          })}
        >
          <Typography variant="body2" fontWeight={500}>
            {t("common.note")}
          </Typography>
          {showSaved && (
            <Typography variant="body2" color="primary">
              {t("multistep.answerSaved")}
            </Typography>
          )}
        </Box>
        <Box p={1} sx={{ backgroundColor: "#DDDEFB" }}>
          <Controller
            name={`${question.code}-comment`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={() => {
                  field.onChange(localValue);
                  field.onBlur();
                }}
                placeholder={t("common.leaveInternalNote")}
                multiline
                rows={4}
                fullWidth
                slotProps={{
                  input: {
                    sx: {
                      padding: 0,
                      "& textarea": {
                        padding: 1,
                        background: "#DDDEFB",
                      },
                      "& fieldset": {
                        display: "none",
                      },
                    },
                  },
                }}
              />
            )}
          />
        </Box>
        <Box
          pt={2}
          sx={(theme) => ({
            padding: theme.spacing(2),
            backgroundColor: "#DDDEFB",
            position: "relative",
          })}
        >
          {note?.attachments && note.attachments.length > 0 && (
            <Box
              display={"flex"}
              gap={1}
              sx={{ background: "#DDDEFB", marginRight: "48px" }}
            >
              {note.attachments.map((file, index) => (
                <FileListItem
                  key={index}
                  file={file}
                  onDelete={() => handleDeleteFile(note, file)}
                />
              ))}
            </Box>
          )}
          {uploadFileEnabled && (
            <Box
              sx={{
                position: "absolute",
                right: "16px",
                bottom: "16px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <IconButton component="label" disabled={isLoadingFormDraftNote}>
                {isLoadingFormDraftNote || isPendingUploadFile ? (
                  <CircularProgress size={24} sx={{ color: "#6D28D9" }} />
                ) : (
                  <FeatherIcon icon="paperclip" size={16} />
                )}
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmationModal
        open={showAIExtractModal}
        onClose={handleAIExtractCancel}
        onConfirm={handleAIExtractConfirm}
        title={t("forms.aiImportTitle")}
        description={t("forms.aiExtractQuestion")}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
      />

      <AIImportDialog
        open={aiImportDialogOpen}
        onClose={handleAIImportDialogClose}
        formId={formBasics.id}
        user={user}
        initialFiles={uploadedFilesForAI}
      />
    </AccordionDetails>
  );
};

export default Note;
